import { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Markdown } from './Markdown';
import { Icon } from './Icon';

/**
 * Per-key editorial DESCRIPTION (key_levels.note, GLOBAL by key name) — the
 * AI/human context for a config key ("what is it, who owns it, which app uses
 * it, where to set non-prod/prod values"). Descriptions can be long + markdown,
 * so the row shows only a compact trigger; the full doc opens in a POPUP that
 * renders markdown and (root/maintainer) edits it. AI writes the same field via
 * the MCP `set_key_note` tool.
 */

export const SET_KEY_NOTE = gql`
  mutation SetKeyNote($keyName: String!, $note: String!) {
    setKeyNote(keyName: $keyName, note: $note)
  }
`;

export function KeyNoteCell({
  keyName,
  note,
  canEdit,
  onChanged,
}: {
  keyName: string;
  note?: string;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);

  if (!note && !canEdit) return null;

  return (
    <>
      {note ? (
        <button type="button" className="key-note-trigger has-note" aria-label={`View description for ${keyName}`} onClick={() => setOpen(true)} title="view / edit description">
          <Icon name="docs" /> <span>View description</span>
        </button>
      ) : (
        <button type="button" className="key-note-trigger" aria-label={`Add description for ${keyName}`} onClick={() => setOpen(true)} title="add a description (root/maintainer)">
          + Add description
        </button>
      )}
      {open && (
        <KeyNoteModal
          keyName={keyName}
          note={note}
          canEdit={canEdit}
          onClose={() => setOpen(false)}
          onChanged={onChanged}
        />
      )}
    </>
  );
}

function KeyNoteModal({
  keyName,
  note,
  canEdit,
  onClose,
  onChanged,
}: {
  keyName: string;
  note?: string;
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(!note && canEdit); // empty + editor → straight to edit
  const [draft, setDraft] = useState(note ?? '');
  const [save, { loading }] = useMutation(SET_KEY_NOTE, {
    onCompleted: () => {
      onChanged();
      onClose();
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={onClose}
    >
      <div
        role="dialog" aria-modal="true" aria-label={`Description for ${keyName}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8,
          width: 'min(760px, 94vw)', maxHeight: '85vh', overflow: 'auto', padding: '18px 20px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <code style={{ fontSize: 14, fontWeight: 600 }}>{keyName}</code>
          <div style={{ display: 'flex', gap: 8 }}>
            {canEdit && !editing && <button onClick={() => setEditing(true)}>Edit</button>}
            <button onClick={onClose} title="Close (Esc)" aria-label="Close description"><Icon name="close" /></button>
          </div>
        </div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
          Description is GLOBAL for this key name (shows on every bundle/env it appears in). Markdown supported.
          Context only — never paste a secret value.
        </div>

        {editing ? (
          <>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={12}
              maxLength={20000}
              autoFocus
              placeholder={'## ' + keyName + '\nWhat is this key? who owns it? which app uses it?\nWhere to set the non-prod vs prod value?'}
              style={{ width: '100%', fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace' }}
            />
            {draft.trim() && (
              <div style={{ marginTop: 10 }}>
                <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>Preview</div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px' }}>
                  <Markdown>{draft}</Markdown>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button disabled={loading} onClick={() => void save({ variables: { keyName, note: draft } })}>
                {loading ? 'Saving…' : 'Save'}
              </button>
              <button
                disabled={loading}
                onClick={() => {
                  setDraft(note ?? '');
                  if (note) setEditing(false);
                  else onClose();
                }}
              >
                Cancel
              </button>
              {note && (
                <button
                  disabled={loading}
                  style={{ marginLeft: 'auto', color: 'var(--muted)' }}
                  title="clear the description"
                  onClick={() => void save({ variables: { keyName, note: '' } })}
                >
                  Clear
                </button>
              )}
            </div>
          </>
        ) : (
          <Markdown>{note ?? ''}</Markdown>
        )}
      </div>
    </div>
  );
}
